import React from 'react';
import { Container, Typography, Box, Stack, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  background: theme.palette.background.paper
}));

const StepCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: (theme.shape.borderRadius as number) * 2,
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.grey[200]}`,
  textAlign: 'center',
  position: 'relative',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4]
  }
}));

const StepNumber = styled(Box)(() => ({
  width: 48,
  height: 48,
  borderRadius: '50%',
  background: '#064E3B',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1.5rem',
  fontWeight: 700,
  margin: '0 auto 16px auto'
}));

const ArrowConnector = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#064E3B',
  fontSize: 32,
  [theme.breakpoints.down('md')]: {
    transform: 'rotate(90deg)',
    margin: theme.spacing(2, 0)
  }
}));

const HowItWorksSection: React.FC = () => {
  const steps = [
    {
      number: 1,
      title: "Create Survey & Set Rewards",
      description: "Design custom surveys or let AI generate them based on your product. You configure the reward amount you'll pay customers for quality reviews."
    },
    {
      number: 2,
      title: "Send to Customers",
      description: "Upload customer lists and send surveys via email, SMS, or messaging apps like WhatsApp, Telegram, and WeChat."
    },
    {
      number: 3,
      title: "AI Generates Reviews",
      description: "Our AI analyzes responses and crafts enthusiastic, authentic reviews that customers can proofread and approve before posting."
    },
    {
      number: 4,
      title: "Post & Pay Rewards",
      description: "Reviews are posted to your chosen platforms (Google, Facebook, Instagram) and you pay customers the rewards you configured via instant UPI payouts."
    }
  ];

  return (
    <SectionContainer>
      <Container maxWidth="lg">
        <Box textAlign="center" sx={{ mb: 8 }}>
          <Typography 
            variant="h2" 
            sx={{ 
              mb: 3,
              color: 'text.primary'
            }}
          >
            How RiteReview Works
          </Typography>
          
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'text.secondary', 
              maxWidth: 600, 
              mx: 'auto',
              lineHeight: 1.6
            }}
          >
            Simple 4-step process to transform your customer feedback into powerful online reviews
          </Typography>
        </Box>

        <Stack 
          direction={{ xs: 'column', md: 'row' }} 
          spacing={{ xs: 2, md: 4 }}
          alignItems="center"
        >
          {steps.map((step, index) => (
            <React.Fragment key={step.number}>
              <Box flex={1}>
                <StepCard elevation={0}>
                  <StepNumber>{step.number}</StepNumber>
                  
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                    {step.title}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    {step.description}
                  </Typography>
                </StepCard>
              </Box>
              
              {index < steps.length - 1 && (
                <ArrowConnector>
                  <ArrowForwardIcon fontSize="inherit" />
                </ArrowConnector>
              )}
            </React.Fragment>
          ))}
        </Stack>

        <Box 
          sx={{ 
            mt: 8, 
            p: 4, 
            background: 'rgba(6, 78, 59, 0.1)',
            borderRadius: 3,
            textAlign: 'center'
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
            Ready to Get Started?
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
            Join thousands of businesses already boosting their online presence with RiteReview
          </Typography>
        </Box>
      </Container>
    </SectionContainer>
  );
};

export default HowItWorksSection;